import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { TrackingHandler } from 'client/tracking/handler';
import { VisitorModel } from 'client/data/models/visitor';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { PRICE_CHECKER_CREATIVE_ID } from 'site-modules/shared/constants/price-checker';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { TrackingConstant } from 'client/tracking/constant';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';

const CTA = 'Get dealers’ best prices';

function CompareOffersUI({
  hasEmbeddedLeadForm = false,
  leadFormDealerCount = null,
  updateLeadFormParams = null,
  style = null,
  city = null,
  zipCode = null,
  leadFormCreativeId = null,
  isMobile = false,
}) {
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle(false);
  const isButtonDisplayTracked = useRef(false);

  useEffect(() => {
    if (!hasEmbeddedLeadForm && leadFormDealerCount && !isButtonDisplayTracked.current) {
      isButtonDisplayTracked.current = true;

      TrackingHandler.fireEvent('pc_show_compare_offers', {
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          value: CTA,
          creative_id: PRICE_CHECKER_CREATIVE_ID,
        },
      });
    }

    if (hasEmbeddedLeadForm && updateLeadFormParams) {
      updateLeadFormParams({
        vehicle: style,
        styleId: get(style, 'id'),
        trim: get(style, 'trimName'),
      });
    }
  });

  if (!leadFormDealerCount || hasEmbeddedLeadForm) {
    return null;
  }

  const creativeId = leadFormCreativeId || CREATIVE_ID.NEW_MULTI_PRICE_CHECKER;

  return (
    <Fragment>
      <Row tag={Card} className="compare-offers flex-lg-row align-items-center mx-0 mt-1_5 px-1 px-lg-2 py-1">
        <Col xs={12} lg={8} className="mb-1 mb-lg-0 text-gray-darker">
          <div className="heading-3 mb-0_5">Don’t have a dealer price quote?</div>
          Get dealer quotes, then come back to compare them.
        </Col>
        <Col
          xs={12}
          lg={4}
          tag={Button}
          color="outline-primary-b"
          size="lg"
          className="compare-offers-btn text-transform-none px-2 py-0_75"
          onClick={toggleLeadFormOpen}
          data-hidedelightedoninteraction
        >
          {CTA}
        </Col>
      </Row>

      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormOpen}
        onDrawerToggle={toggleLeadFormOpen}
        params={{
          styleId: style.id,
          trim: style.trimName,
          vehicle: style,
          zipCode,
          city,
        }}
        options={{
          isMobile,
          creativeId,
          inventoryTextDesktop: "We'll show you the price you should pay.",
        }}
        creativeId={creativeId}
      />
    </Fragment>
  );
}

CompareOffersUI.propTypes = {
  updateLeadFormParams: PropTypes.func,
  style: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    year: PropTypes.number,
    trimName: PropTypes.string,
    id: PropTypes.number,
    make: PropTypes.shape({ name: PropTypes.string }),
    model: PropTypes.shape({ name: PropTypes.string }),
    submodels: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
  }),
  leadFormDealerCount: PropTypes.number,
  city: PropTypes.string,
  zipCode: PropTypes.string,
  leadFormCreativeId: PropTypes.string,
  hasEmbeddedLeadForm: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { leadFormDealerCount, style }) =>
  leadFormDealerCount === null && !!get(style, 'makeName');
export const CompareOffers = connectToModel(React.memo(CompareOffersUI, propsAreEqual), {
  zipCode: bindToPath('location.zipCode', VisitorModel),
  city: bindToPath('location.city', VisitorModel),
  leadFormDealerCount: bindToPath(
    ({ style }) =>
      style &&
      getUnifiedLeadFormDealerCountV2Path({
        makeSlug: makeNiceName(style.makeName),
        modelSlug: makeNiceName(style.modelName),
        strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
      }),
    UnifiedLeadFormModel
  ),
});
